import * as React from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "@/components/Modal.js";
import { SearchableSelect } from "@/components/SearchableSelect.js";
import { TagInput } from "@/components/TagInput.js";
import { apiFetch } from "@/lib/api.js";
import type { Space } from "@/hooks/useSpaces.js";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  spaces: Space[];
  onUpdated: () => void;
  onSpaceCreated?: () => void;
}

export function BulkEditModal({ open, onClose, selectedIds, spaces, onUpdated, onSpaceCreated }: Props) {
  const [spaceId, setSpaceId] = React.useState<string | null>(null);
  const [tags, setTags] = React.useState<string[]>([]);
  const [tagMode, setTagMode] = React.useState<"add" | "replace">("add");
  // "none" = leave untouched; "flag"/"clear" = set the Re-locate designation on
  // all selected items (P0-B, R-B1 bulk/multi-select).
  const [relocate, setRelocate] = React.useState<"none" | "flag" | "clear">("none");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => { if (open) { setSpaceId(null); setTags([]); setTagMode("add"); setRelocate("none"); setError(""); } }, [open]);

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      const body: any = { ids: selectedIds };
      if (spaceId !== null) body.spaceId = spaceId;
      if (tags.length > 0) { body.tags = tags; body.tagUpdateMode = tagMode; }
      if (relocate !== "none") body.relocate = relocate === "flag";
      await apiFetch("/api/items/bulk", { method: "PUT", body: JSON.stringify(body) });
      onUpdated(); onClose();
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit ${selectedIds.length} Items`}>
      {error && <p className="text-[0.8125rem] text-error-text mb-3">{error}</p>}
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-body mb-1 block">Move to location</label>
          <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} placeholder="Keep current location" />
        </div>
        <div>
          <label className="text-sm font-medium text-body mb-1 block">Tags</label>
          <div className="flex gap-2 mb-2">
            {(["add", "replace"] as const).map((m) => (
              <button key={m} onClick={() => setTagMode(m)} className={`px-3 py-1 rounded-pill text-[0.8125rem] font-medium transition-colors ${tagMode === m ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-clay-muted/70"}`}>
                {m === "add" ? "Add tags" : "Replace tags"}
              </button>
            ))}
          </div>
          <TagInput tags={tags} onChange={setTags} />
        </div>
        <div>
          <label className="text-sm font-medium text-body mb-1 block">Flag to move</label>
          <div className="flex gap-2">
            {([["none", "Leave as-is"], ["flag", "Flag to move"], ["clear", "Clear flag"]] as const).map(([m, label]) => (
              <button key={m} onClick={() => setRelocate(m)} className={`px-3 py-1 rounded-pill text-[0.8125rem] font-medium transition-colors ${relocate === m ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-clay-muted/70"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[0.8125rem] text-faint mt-1">Adds/removes selected items on your Re-locate list. Doesn't move them.</p>
        </div>
        <button onClick={handleSave} disabled={loading} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Apply Changes"}
        </button>
      </div>
    </Modal>
  );
}
